/**
 * CronJobsAdminPage — standalone admin page for pg_cron job management.
 */
import { AlertCircle, Clock } from 'lucide-react';
import React, { useMemo } from 'react';

import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuthentication } from '../../hooks/useAuthentication';
import { isOwnerAdminEmail } from '../../modules/dashboard/components/Jobs/helpers';
import Layout, { type DashboardPage } from '../../modules/dashboard/components/Layout';
import CronJobsTabSection from '../../modules/dashboard/components/cron/CronJobsTabSection';

interface CronJobsAdminPageProps {
  onSignOut?: () => void | Promise<void>;
  /** When true the component renders its content directly without the Layout shell */
  embedded?: boolean;
  /** When embedded in Account Settings, parent passes tab visibility */
  isActive?: boolean;
}

export default function CronJobsAdminPage({
  onSignOut = () => {},
  embedded = false,
  isActive = true,
}: CronJobsAdminPageProps) {
  const { session, user, loading: authLoading } = useAuthentication();
  const isAdmin = useMemo(
    () => isOwnerAdminEmail(user?.email ?? session?.user?.email),
    [user?.email, session?.user?.email],
  );

  const handleNavigate = (page: DashboardPage) => {
    const paths: Partial<Record<DashboardPage, string>> = {
      dashboard: '/dashboard',
      profile: '/profile',
      'cron-jobs-admin': '/admin/cron-jobs',
    };
    const path = paths[page];
    if (path && window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!session || !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[320px] gap-3 px-4 text-center">
        <AlertCircle className="w-8 h-8 text-red-500" />
        <p className="text-[14px] font-medium">Owner admin access required.</p>
      </div>
    );
  }

  const content = (
    <div className="ds-cron-jobs-admin ds-control-panel px-2 sm:px-4 lg:px-6 py-4 sm:py-6 max-w-[1440px] mx-auto w-full min-w-0 space-y-4">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'var(--brand-purple-soft, rgba(108,71,255,0.10))', color: 'var(--brand-purple, #6C47FF)' }}
        >
          <Clock className="w-5 h-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="ds-cp-page-title ggj-page-title text-2xl sm:text-3xl font-bold leading-tight">
            Cron Jobs
          </h1>
          <p className="ds-cp-page-desc text-[13px] sm:text-sm mt-0.5">
            Scheduled pg_cron jobs, execution history, and manual triggers.
          </p>
        </div>
      </div>

      <CronJobsTabSection isActive={isActive} />
    </div>
  );

  if (embedded) return content;

  return (
    <Layout currentPage="cron-jobs-admin" onNavigate={handleNavigate} onSignOut={onSignOut}>
      {content}
    </Layout>
  );
}
